// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { BundleApplyLine } from '@/bundle/components/apply-bundle-dialog';

/**
 * A current line-item row projected to the fields the auto-sum needs:
 * the row's item id (undefined for an empty row) and its quantity.
 */
export interface ExistingBundleRow {
  itemId: string | undefined;
  quantity: number | null;
}

export interface BundleAutoSumResult {
  /**
   * Existing rows whose quantity must increase, keyed by row index, carrying
   * the new summed quantity.
   */
  updates: { index: number; quantity: number }[];
  /**
   * Bundle members that have no existing row and append as new lines, in
   * bundle member order.
   */
  appends: BundleApplyLine[];
  /**
   * Bundle members that reuse a trailing blank row instead of appending, keyed
   * by row index. Filling a blank row does not grow the row count.
   */
  fills: { index: number; line: BundleApplyLine }[];
  /**
   * True when at least one member summed into an existing row, so the caller
   * can show the "combined" toast variant.
   */
  combined: boolean;
}

/**
 * Computes how to apply a bundle to a surface that forbids duplicate SKU
 * rows (FO / PO / RO / shipment): each member whose item already has a row
 * increases that row's quantity by the computed amount (auto-sum); members
 * without an existing row append as new lines. The result is computed
 * against a single snapshot of the current rows, so callers can apply the
 * updates, fills, and appends independently and in any order.
 *
 * A trailing row with no item selected cannot be submitted, so the first
 * appended member fills it rather than leaving a blank row behind.
 */
export function computeBundleAutoSum(
  existingRows: ExistingBundleRow[],
  lines: BundleApplyLine[]
): BundleAutoSumResult {
  const indexByItemId = new Map<string, number>();
  existingRows.forEach((row, index) => {
    if (row.itemId !== undefined && !indexByItemId.has(row.itemId)) {
      indexByItemId.set(row.itemId, index);
    }
  });

  const appends: BundleApplyLine[] = [];
  const summedByIndex = new Map<number, number>();

  for (const line of lines) {
    const existingIndex = indexByItemId.get(line.item.id);
    if (existingIndex === undefined) {
      appends.push(line);
      continue;
    }
    const base =
      summedByIndex.get(existingIndex) ??
      existingRows[existingIndex].quantity ??
      0;
    summedByIndex.set(existingIndex, base + line.quantity);
  }

  const updates = [...summedByIndex.entries()].map(([index, quantity]) => ({
    index,
    quantity,
  }));

  const lastIndex = existingRows.length - 1;
  const hasTrailingBlankRow =
    lastIndex >= 0 && existingRows[lastIndex].itemId === undefined;
  const fills =
    hasTrailingBlankRow && appends.length > 0
      ? [{ index: lastIndex, line: appends[0] }]
      : [];

  return {
    updates,
    appends: appends.slice(fills.length),
    fills,
    combined: updates.length > 0,
  };
}
